import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Download, Upload, EyeOff, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { resolveAiqProduct, setProductInvoiceBasis } from '../../lib/api';
import { PRODUCT_STATUS, errMsg } from './aiqMeta';
import { StatusBadge, StatusChips, DiffList } from './SyncBits';
import { LinkCodePopover } from './LinkCodePopover';
import { InvoiceBasisSelect } from './InvoiceBasisSelect';

const rowKey = (r) => r.product_id || `aiq-${r.code}`;

const ProductActions = ({ row, busy, act }) => {
  const btn = 'h-7 text-xs';
  if (row.status === 'differs') {
    return (
      <div className="flex gap-1.5 justify-end flex-wrap">
        <Button size="sm" variant="outline" className={btn} disabled={busy}
          onClick={() => act({ action: 'keep_app', product_id: row.product_id }, 'AccountsIQ updated to match the app',
            `Update stock item ${row.code} in AccountsIQ with the app's name/status?`)}
          data-testid={`prod-keep-app-${row.product_id}`}>
          <ArrowUpFromLine className="w-3 h-3 mr-1" /> Keep app
        </Button>
        <Button size="sm" variant="outline" className={btn} disabled={busy}
          onClick={() => act({ action: 'keep_aiq', product_id: row.product_id }, 'App updated from AccountsIQ')}
          data-testid={`prod-keep-aiq-${row.product_id}`}>
          <ArrowDownToLine className="w-3 h-3 mr-1" /> Keep AccountsIQ
        </Button>
        <Button size="sm" variant="ghost" className={btn} disabled={busy} title="Leave both as they are"
          onClick={() => act({ action: 'dismiss', product_id: row.product_id }, 'Difference dismissed')}
          data-testid={`prod-dismiss-${row.product_id}`}>
          <EyeOff className="w-3 h-3" />
        </Button>
      </div>
    );
  }
  if (row.status === 'aiq_only') {
    return (
      <div className="flex gap-1.5 justify-end">
        <Button size="sm" variant="outline" className={btn} disabled={busy}
          onClick={() => act({ action: 'import', code: row.code }, `${row.name} added to the app`)} data-testid={`prod-import-${row.code}`}>
          <Download className="w-3 h-3 mr-1" /> Import
        </Button>
        <Button size="sm" variant="ghost" className={btn} disabled={busy}
          onClick={() => act({ action: 'dismiss', code: row.code }, 'Ignored')} data-testid={`prod-ignore-${row.code}`}>
          <EyeOff className="w-3 h-3" />
        </Button>
      </div>
    );
  }
  if (row.status === 'not_in_aiq' || row.status === 'no_code') {
    return (
      <div className="flex gap-1.5 justify-end">
        {row.code && (
          <Button size="sm" variant="outline" className={btn} disabled={busy}
            onClick={() => act({ action: 'create_in_aiq', product_id: row.product_id }, `${row.code} created in AccountsIQ`,
              `Create stock item ${row.code} "${row.name}" in AccountsIQ?`)}
            data-testid={`prod-create-${row.product_id}`}>
            <Upload className="w-3 h-3 mr-1" /> Create in AccountsIQ
          </Button>
        )}
        <LinkCodePopover initial="" label="Link" busy={busy} testId={`prod-link-${row.product_id}`}
          onSave={(code) => act({ action: 'link', product_id: row.product_id, link_code: code }, `Linked to ${code}`)} />
      </div>
    );
  }
  return null;
};

const ProductRow = ({ row, busy, act, onBasis }) => (
  <tr className="hover:bg-zinc-50/70 align-top" data-testid={`prod-row-${rowKey(row)}`}>
    <td className="px-3 py-2 text-sm">{row.product_id ? row.name : <span className="text-zinc-400">—</span>}</td>
    <td className="px-3 py-2 font-mono text-xs">{row.code || '—'}</td>
    <td className="px-3 py-2 text-sm">{row.aiq?.description || <span className="text-zinc-400">—</span>}</td>
    <td className="px-3 py-2"><StatusBadge meta={PRODUCT_STATUS} status={row.status} testId={`prod-status-${rowKey(row)}`} /></td>
    <td className="px-3 py-2">{row.status === 'differs' ? <DiffList diffs={row.diffs} /> : null}</td>
    <td className="px-3 py-2">
      {row.product_id && (
        <InvoiceBasisSelect value={row.invoice_basis} onChange={(b) => onBasis(row, b)} disabled={busy} testId={`prod-basis-${row.product_id}`} />
      )}
    </td>
    <td className="px-3 py-2 text-right"><ProductActions row={row} busy={busy} act={act} /></td>
  </tr>
);

export const ProductSyncTable = ({ data, onChanged }) => {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState(150);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.rows || []).filter((r) => (filter === 'all' || r.status === filter)
      && (!q || [r.name, r.code, r.aiq?.description].some((v) => (v || '').toLowerCase().includes(q))));
  }, [data, filter, query]);

  const act = async (payload, success, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return false;
    setBusy(true);
    try {
      await resolveAiqProduct(payload);
      toast.success(success);
      await onChanged();
      return true;
    } catch (e) {
      toast.error(errMsg(e, 'Action failed'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onBasis = async (row, basis) => {
    try {
      await setProductInvoiceBasis(row.product_id, basis);
      toast.success(`${row.name}: invoiced ${basis === 'kg' ? 'per kg' : `per ${basis.replace(/s$/, '')}`}`);
      await onChanged();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save'));
    }
  };

  return (
    <div className="space-y-3" data-testid="aiq-product-review">
      <p className="text-xs text-zinc-500">Products sync both ways, matched on the stock code (SKU). Every difference waits for your decision. "Invoice by" sets how each product is quantified on invoices.</p>
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <StatusChips meta={PRODUCT_STATUS} counts={data?.counts} value={filter} onChange={setFilter} testPrefix="prod" />
        <Input placeholder="Search name or code…" value={query} onChange={(e) => setQuery(e.target.value)} className="h-8 w-56" data-testid="prod-search" />
      </div>
      <div className="border rounded-md overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 text-[11px] uppercase text-zinc-600 text-left">
              <th className="px-3 py-2">App product</th><th className="px-3 py-2">Stock code</th>
              <th className="px-3 py-2">AccountsIQ description</th><th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Differences (app → AccountsIQ)</th><th className="px-3 py-2">Invoice by</th>
              <th className="px-3 py-2 text-right">Decision</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.slice(0, limit).map((r) => <ProductRow key={rowKey(r)} row={r} busy={busy} act={act} onBasis={onBasis} />)}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-zinc-500" data-testid="prod-empty">Nothing here.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {rows.length > limit && (
        <Button variant="ghost" size="sm" onClick={() => setLimit((l) => l + 150)} data-testid="prod-show-more">Show more ({rows.length - limit} left)</Button>
      )}
    </div>
  );
};
